import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import {
  Search, Leaf, Sun, Moon, Sprout, Flower2, Palmtree, Compass, ChevronRight, ArrowLeft, Sparkles,
  Droplet, Thermometer, Wind, Gauge, CheckCircle2,
} from "lucide-react";

const ICON_MAP = {
  leaf: Leaf, sun: Sun, sprout: Sprout, palm: Palmtree, moon: Moon, flower: Flower2,
};

const speciesSlug = (sci) =>
  (sci || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/\./g, "")
    .replace(/'/g, "");

function CategoryTile({ category, onClick }) {
  const Icon = ICON_MAP[category.icon] || Leaf;
  return (
    <button
      onClick={onClick}
      className="glass-card p-4 text-left fade-up transition-all hover:scale-[1.02]"
      data-testid={`explore-category-${category.id}`}
      style={{
        background: "rgba(0,0,0,0.72)",
        border: "1px solid rgba(76,175,80,0.32)",
        borderRadius: 18,
      }}
    >
      <span
        className="inline-flex w-11 h-11 rounded-full items-center justify-center mb-2"
        style={{ background: "rgba(76,175,80,0.22)", border: "1px solid rgba(76,175,80,0.5)" }}
      >
        <Icon size={20} className="text-[#4CAF50]" strokeWidth={1.7} />
      </span>
      <p className="text-sm font-bold text-white" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>
        {category.label}
      </p>
      <p className="text-[12px] mt-0.5" style={{ color: "rgba(255,255,255,0.85)", textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>
        {category.subtitle}
      </p>
      <p className="text-[10px] uppercase tracking-wider mt-2 font-semibold" style={{ color: "#7BC67B" }}>
        {category.count} species
      </p>
    </button>
  );
}

function CollectionCard({ collection, onClick }) {
  return (
    <button
      onClick={onClick}
      className="glass-card w-full text-left p-4 fade-up transition-all hover:scale-[1.01]"
      data-testid={`explore-collection-${collection.id}`}
      style={{
        background: "rgba(0,0,0,0.72)",
        border: "1px solid rgba(76,175,80,0.32)",
        borderRadius: 18,
      }}
    >
      <div className="flex items-start gap-3">
        <span
          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(255,213,79,0.20)", border: "1px solid rgba(255,213,79,0.55)" }}
        >
          <Sparkles size={18} className="text-[#FFD54F]" strokeWidth={1.7} />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-white" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>
            {collection.title}
          </h3>
          <p className="text-[12px] mt-0.5" style={{ color: "rgba(255,255,255,0.85)", textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>
            {collection.subtitle}
          </p>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {(collection.preview || []).map((p, i) => (
              <span
                key={i}
                className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                style={{ background: "rgba(76,175,80,0.25)", color: "#A5D6A7", border: "1px solid rgba(76,175,80,0.5)" }}
              >
                {p.common_name}
              </span>
            ))}
            <span className="text-[10px] font-medium" style={{ color: "rgba(255,255,255,0.7)" }}>
              +{Math.max((collection.count || 0) - (collection.preview || []).length, 0)} more
            </span>
          </div>
        </div>
        <ChevronRight size={16} className="text-[#4CAF50] flex-shrink-0 mt-1" />
      </div>
    </button>
  );
}

function SpeciesRow({ species, idx, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`glass-card w-full px-4 py-3 flex items-center gap-3 text-left fade-up stagger-${(idx % 5) + 1}`}
      style={{ background: "rgba(0,0,0,0.72)", border: "1px solid rgba(76,175,80,0.32)" }}
      data-testid={`explore-species-${idx}`}
    >
      <span
        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: "rgba(76,175,80,0.22)", border: "1px solid rgba(76,175,80,0.5)" }}
      >
        <Leaf size={18} className="text-[#4CAF50]" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-white truncate" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>
          {species.common_name}
        </p>
        <p className="text-[12px] italic truncate font-medium" style={{ color: "#A5D6A7" }}>
          {species.scientific_name}
        </p>
      </div>
      <ChevronRight size={14} className="text-[#4CAF50] flex-shrink-0" />
    </button>
  );
}

export default function SearchScreen() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const navigate = useNavigate();

  // Load home view (categories + collections + popular)
  useEffect(() => {
    (async () => {
      try {
        const [cats, cols] = await Promise.all([
          api.get("/explore/categories"),
          api.get("/explore/collections"),
        ]);
        setCategories(cats.data.categories || []);
        setCollections(cols.data.collections || []);
      } catch (err) {
        console.error("[Explore] load failed:", err);
      }
    })();
  }, []);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/species/search?q=${encodeURIComponent(q)}`);
        setResults(data.results || []);
      } catch (err) {
        console.error("[Explore] search failed:", err);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const hasQuery = useMemo(() => q.trim().length > 0, [q]);

  return (
    <Shell section="search">
      <div className="flex items-center gap-2 mb-3 fade-up">
        <span
          className="inline-flex items-center justify-center w-10 h-10 rounded-full"
          style={{ background: "rgba(76,175,80,0.25)", boxShadow: "var(--glow-green)", border: "1px solid rgba(76,175,80,0.5)" }}
        >
          <Compass size={18} className="text-[#4CAF50]" strokeWidth={1.6} />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-shadow-green" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.7), 0 0 14px rgba(76,175,80,0.45)" }}>
            Explore plants
          </h1>
          <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "rgba(255,255,255,0.85)", textShadow: "0 1px 4px rgba(0,0,0,0.65)" }}>
            400,000+ species · curated collections
          </p>
        </div>
      </div>

      <div className="glass-card flex items-center gap-3 px-4 py-3 mb-5 fade-up stagger-1"
           data-testid="search-bar" style={{ background: "rgba(0,0,0,0.72)", border: "1px solid rgba(76,175,80,0.32)" }}>
        <Search size={18} className="text-[#4CAF50]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by plant name…"
          className="flex-1 bg-transparent outline-none text-white text-sm font-medium"
          style={{ "--tw-placeholder-opacity": "1" }}
          data-testid="search-input"
        />
      </div>

      {hasQuery ? (
        <>
          <h2 className="text-[12px] uppercase tracking-wider mb-2 font-bold" style={{ color: "#FFFFFF", textShadow: "0 1px 4px rgba(0,0,0,0.7)" }}>
            {results.length} result{results.length !== 1 ? "s" : ""}
          </h2>
          <div className="space-y-2" data-testid="search-results">
            {results.map((r, idx) => (
              <SpeciesRow key={r.scientific_name} species={r} idx={idx} onClick={() => navigate(`/species/${r.slug || speciesSlug(r.scientific_name)}`)} />
            ))}
            {results.length === 0 && (
              <div className="glass-card p-6 text-center" style={{ background: "rgba(0,0,0,0.72)" }}>
                <p className="text-white text-sm font-medium">No plants found. Try a different name.</p>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {categories.length > 0 && (
            <>
              <h2 className="text-[12px] uppercase tracking-wider mb-2 fade-up stagger-2 font-bold" style={{ color: "#FFFFFF", textShadow: "0 1px 4px rgba(0,0,0,0.7)" }}>
                Browse by category
              </h2>
              <div className="grid grid-cols-2 gap-2.5 mb-6" data-testid="explore-categories">
                {categories.map((c) => (
                  <CategoryTile key={c.id} category={c} onClick={() => navigate(`/explore/category/${c.id}`)} />
                ))}
              </div>
            </>
          )}

          {collections.length > 0 && (
            <>
              <h2 className="text-[12px] uppercase tracking-wider mb-2 fade-up stagger-3 font-bold" style={{ color: "#FFFFFF", textShadow: "0 1px 4px rgba(0,0,0,0.7)" }}>
                Curated collections
              </h2>
              <div className="space-y-2.5 mb-6" data-testid="explore-collections">
                {collections.map((c) => (
                  <CollectionCard key={c.id} collection={c} onClick={() => navigate(`/explore/collection/${c.id}`)} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </Shell>
  );
}

/** Drill-down view for `/explore/category/:id` and `/explore/collection/:id` */
export function ExploreListScreen({ kind }) {
  const params = useParams();
  const navigate = useNavigate();
  const id = params.id;
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const url = kind === "category" ? `/explore/categories/${id}` : `/explore/collections/${id}`;
        const res = await api.get(url);
        setData(res.data);
      } catch (err) {
        console.error("[Explore] list failed:", err);
      }
    })();
  }, [kind, id]);

  const title = data?.category?.label || data?.collection?.title || "Loading…";
  const subtitle = data?.category?.subtitle || data?.collection?.subtitle || "";
  const species = data?.species || [];
  const category = kind === "category" ? data?.category : null;
  const HeroIcon = category && ICON_MAP[category.icon] ? ICON_MAP[category.icon] : Leaf;

  return (
    <Shell section="search">
      <button
        onClick={() => navigate("/search")}
        className="flex items-center gap-1 text-[#4CAF50] text-sm mb-3 hover:underline"
        data-testid="explore-list-back"
      >
        <ArrowLeft size={14} /> Explore
      </button>

      {/* Hero block */}
      <div
        className="glass-card-strong p-5 mb-5 fade-up"
        style={{ borderColor: "rgba(76,175,80,0.35)", boxShadow: "0 12px 28px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset" }}
        data-testid="explore-hero"
      >
        <div className="flex items-start gap-4">
          {category && (
            <span
              className="inline-flex items-center justify-center w-14 h-14 rounded-2xl flex-shrink-0"
              style={{ background: "rgba(76,175,80,0.18)", boxShadow: "var(--glow-green)" }}
            >
              <HeroIcon size={26} className="text-[#4CAF50]" strokeWidth={1.6} />
            </span>
          )}
          <div className="min-w-0">
            <h1 className="text-2xl font-extrabold leading-tight" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.7), 0 0 14px rgba(76,175,80,0.4)" }}>
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm font-medium mt-1" style={{ color: "rgba(255,255,255,0.85)", textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {category?.description && (
          <p className="text-sm leading-relaxed mt-4 text-soft" data-testid="category-description" style={{ color: "rgba(255,255,255,0.92)" }}>
            {category.description}
          </p>
        )}

        {Array.isArray(category?.characteristics) && category.characteristics.length > 0 && (
          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-2">Key characteristics</p>
            <ul className="space-y-1.5" data-testid="category-characteristics">
              {category.characteristics.map((c) => (
                <li key={c} className="flex items-start gap-2 text-sm text-soft">
                  <CheckCircle2 size={14} className="text-[#4CAF50] mt-0.5 flex-shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {category?.care_summary && Object.keys(category.care_summary).length > 0 && (
          <div className="mt-5 grid grid-cols-2 gap-2.5" data-testid="category-care-summary">
            {category.care_summary.light && (
              <div className="rounded-xl p-3" style={{ background: "rgba(8,14,10,0.55)", border: "1px solid rgba(255,215,0,0.25)" }}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Sun size={12} className="text-[#FFD700]" />
                  <span className="text-[10px] uppercase tracking-wider text-muted-soft">Light</span>
                </div>
                <p className="text-xs text-soft leading-snug">{category.care_summary.light}</p>
              </div>
            )}
            {category.care_summary.water && (
              <div className="rounded-xl p-3" style={{ background: "rgba(8,14,10,0.55)", border: "1px solid rgba(33,150,243,0.25)" }}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Droplet size={12} className="text-[#4FC3F7]" />
                  <span className="text-[10px] uppercase tracking-wider text-muted-soft">Water</span>
                </div>
                <p className="text-xs text-soft leading-snug">{category.care_summary.water}</p>
              </div>
            )}
            {category.care_summary.humidity && (
              <div className="rounded-xl p-3" style={{ background: "rgba(8,14,10,0.55)", border: "1px solid rgba(76,175,80,0.25)" }}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Wind size={12} className="text-[#4CAF50]" />
                  <span className="text-[10px] uppercase tracking-wider text-muted-soft">Humidity</span>
                </div>
                <p className="text-xs text-soft leading-snug">{category.care_summary.humidity}</p>
              </div>
            )}
            {category.care_summary.difficulty && (
              <div className="rounded-xl p-3" style={{ background: "rgba(8,14,10,0.55)", border: "1px solid rgba(255,143,0,0.25)" }}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Gauge size={12} className="text-[#FF8F00]" />
                  <span className="text-[10px] uppercase tracking-wider text-muted-soft">Difficulty</span>
                </div>
                <p className="text-xs text-soft leading-snug">{category.care_summary.difficulty}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Species list */}
      {species.length > 0 && (
        <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-2" data-testid="explore-species-heading">
          {species.length} species in this category
        </p>
      )}
      {species.length === 0 ? (
        <p className="text-white text-sm py-10 text-center font-medium" data-testid="explore-list-empty" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>
          {data ? "No species in this list yet." : "Loading…"}
        </p>
      ) : (
        <div className="space-y-2" data-testid="explore-list-species">
          {species.map((s, idx) => (
            <SpeciesRow key={s.scientific_name} species={s} idx={idx} onClick={() => navigate(`/species/${s.slug || speciesSlug(s.scientific_name)}`)} />
          ))}
        </div>
      )}
    </Shell>
  );
}
